import { useState } from "react";
import { motion } from "framer-motion";
import { useConsultation } from "../components/Consultation";
import { Reveal, Tag, LineReveal, SolidButton, EASE } from "../components/Shared";

const STEPS = [
  { n: "01", title: "IMAGINE", body: "Your vision begins with a conversation. Occasion, mood, memory — we listen first." },
  { n: "02", title: "CREATE", body: "Silhouette, fabric, colour and detail come together in sketch and drape." },
  { n: "03", title: "REVEAL", body: "A garment created exclusively for you. From first sketch to final fitting." },
];

const Bespoke = () => {
  const { open } = useConsultation();
  const [active, setActive] = useState(0);

  return (
    <section className="relative overflow-hidden bg-ink px-5 py-28 sm:px-10 md:py-44" data-testid="bespoke-section">
      <div className="pointer-events-none absolute -right-24 top-10 select-none font-serif text-[26vw] font-light leading-none text-bone/[0.05]">RB</div>
      <div className="mx-auto max-w-[1600px]">
        <Reveal><Tag>Bespoke Experience</Tag></Reveal>
        <LineReveal
          lines={["MADE FOR YOU.", "NOT FOR EVERYONE."]}
          className="mt-8 font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-7xl lg:text-8xl"
          lineClassName="last:text-flame"
        />
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-lg text-lg font-light leading-relaxed text-fog" data-testid="bespoke-copy">
            From first sketch to final fitting, every bespoke creation begins with an idea — yours.
          </p>
        </Reveal>

        <div className="mt-20 grid gap-px bg-line/10 md:grid-cols-3" data-testid="bespoke-steps">
          {STEPS.map((s, i) => (
            <motion.button
              key={s.n}
              type="button"
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onClick={() => setActive(i)}
              data-testid={`bespoke-step-${s.n}`}
              className={`relative bg-ink p-10 text-left transition-colors duration-500 ${active === i ? "bg-coal" : ""}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: EASE }}
            >
              <motion.span
                className="absolute left-0 top-0 h-0.5 bg-flame"
                animate={{ width: active === i ? "100%" : "0%" }}
                transition={{ duration: 0.6, ease: EASE }}
              />
              <span className={`font-mono text-sm transition-colors duration-300 ${active === i ? "text-flame" : "text-fog"}`}>{s.n}</span>
              <h3 className="mt-4 font-serif text-4xl font-light tracking-wide">{s.title}</h3>
              <p className={`mt-4 max-w-xs text-sm font-light leading-relaxed transition-colors duration-300 ${active === i ? "text-bone/80" : "text-fog"}`}>{s.body}</p>
            </motion.button>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-16 flex flex-wrap items-center gap-10">
          <SolidButton onClick={open} testId="bespoke-book-button">Book a Bespoke Consultation</SolidButton>
          <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-fog" data-testid="bespoke-audience">
            <span className="transition-colors hover:text-flame">Women</span>
            <span className="text-flame">|</span>
            <span className="transition-colors hover:text-flame">Men</span>
            <span className="text-flame">|</span>
            <span className="transition-colors hover:text-flame">Kids</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Bespoke;
